import { useDigestPlayer } from '@/contexts/digest-player-context';
import { WorkflowBadge } from '@digests/components/workflow-badge';
import { Link } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ExternalLink, Loader2, Pause, Play, SkipBack, SkipForward, X } from 'lucide-react';

export function DigestAudioPlayer() {
    const { t } = useTranslation();
    const {
        playlist,
        currentItemIndex,
        isPlaying,
        isLoading,
        currentTime,
        togglePlayPause,
        next,
        previous,
        pause,
        error,
        setState,
        activeDigestWorkflowUuid,
        activeDigestWorkflowName,
        activeDigestWorkflowColor,
    } = useDigestPlayer();

    const currentItem = currentItemIndex !== null && playlist.length > currentItemIndex ? playlist[currentItemIndex] : null;

    const formatTime = (seconds: number) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const [rightOffset, setRightOffset] = useState(24);

    useEffect(() => {
        const calculateOffset = () => {
            const windowWidth = window.innerWidth;
            const maxContentWidth = 1280;
            if (windowWidth > maxContentWidth) {
                const sideSpace = (windowWidth - maxContentWidth) / 2;
                setRightOffset(sideSpace);
            } else {
                setRightOffset(24);
            }
        };

        calculateOffset();
        window.addEventListener('resize', calculateOffset);

        return () => {
            window.removeEventListener('resize', calculateOffset);
        };
    }, []);

    if (currentItemIndex === null || !currentItem) return null;

    const workflowName = activeDigestWorkflowName || 'Digest';

    return (
        <div className="fixed bottom-16 z-50 mr-6 mb-6 w-72" style={{ right: `${rightOffset}px` }}>
            <div className="border-border/40 bg-background/90 rounded-xl border p-1.5 shadow-lg backdrop-blur-sm transition-all duration-300">
                <div className="border-border/80 bg-secondary text-secondary-foreground relative overflow-hidden rounded-lg border shadow-sm">
                    <div className="px-3 py-2.5">
                        <div className="mb-1.5 flex items-center justify-between">
                            {activeDigestWorkflowUuid ? (
                                <Link
                                    href={route('workflow.filter', { workflow: activeDigestWorkflowUuid })}
                                    className="group flex max-w-[80%] items-center gap-2.5 truncate pr-2 transition-opacity hover:opacity-90"
                                    title={`View rule: ${workflowName}`}
                                >
                                    {activeDigestWorkflowColor && (
                                        <WorkflowBadge
                                            name={workflowName}
                                            color={activeDigestWorkflowColor}
                                            showLabel={false}
                                            className="h-2.5 w-2.5 flex-shrink-0 !border-0"
                                        />
                                    )}
                                    <span className="truncate text-sm font-medium">{workflowName}</span>
                                    <ExternalLink className="text-secondary-foreground/50 group-hover:text-secondary-foreground/80 h-3 w-3 flex-shrink-0 opacity-0 transition-colors group-hover:opacity-100" />
                                </Link>
                            ) : (
                                <span className="truncate pr-2 text-sm font-medium">{currentItem.title}</span>
                            )}

                            <Button
                                variant="ghost"
                                size="icon"
                                className="text-secondary-foreground/50 hover:text-secondary-foreground hover:bg-secondary-foreground/10 h-7 w-7 p-0"
                                title={t('ui.close_player')}
                                onClick={() => {
                                    if (isPlaying) pause();
                                    setState((prev) => ({
                                        ...prev,
                                        activeDigestId: null,
                                        activeDigestWorkflowUuid: null,
                                        activeDigestWorkflowName: null,
                                        activeDigestWorkflowColor: null,
                                        playlist: [],
                                        currentItemIndex: null,
                                        isPlaying: false,
                                        currentTime: 0,
                                        error: null,
                                    }));
                                }}
                            >
                                <X className="h-3.5 w-3.5" />
                            </Button>
                        </div>

                        <div className="text-muted-foreground mb-1.5 truncate text-xs" title={currentItem.title}>
                            {currentItem.type === 'summary' ? t('digest_player.summary_title') : `Email: ${currentItem.title}`}
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <Button
                                    onClick={previous}
                                    variant="ghost"
                                    size="icon"
                                    className="text-secondary-foreground hover:text-secondary-foreground hover:bg-secondary-foreground/10 h-7 w-7"
                                    disabled={currentItemIndex === 0}
                                >
                                    <SkipBack className="h-3.5 w-3.5" />
                                </Button>

                                <Button
                                    onClick={togglePlayPause}
                                    variant="ghost"
                                    size="icon"
                                    className="text-secondary-foreground hover:text-secondary-foreground hover:bg-secondary-foreground/10 h-8 w-8"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : isPlaying ? (
                                        <Pause className="h-4 w-4" />
                                    ) : (
                                        <Play className="h-4 w-4" />
                                    )}
                                </Button>

                                <Button
                                    onClick={next}
                                    variant="ghost"
                                    size="icon"
                                    className="text-secondary-foreground hover:text-secondary-foreground hover:bg-secondary-foreground/10 h-7 w-7"
                                    disabled={currentItemIndex === playlist.length - 1}
                                >
                                    <SkipForward className="h-3.5 w-3.5" />
                                </Button>
                            </div>

                            <div className="text-secondary-foreground/80 text-xs tabular-nums">{formatTime(currentTime)}</div>
                        </div>

                        {error && (
                            <div className="bg-destructive/10 text-destructive mt-2 flex items-center justify-between rounded-md p-1.5 text-xs">
                                <span className="truncate">{error}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
